import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  assessmentsApi,
  healthApi,
  normalizeStatsPayload,
  type DbHealth,
  type Stats,
  type TimeseriesPoint,
  type VersionInfo,
} from '../lib/api';
import TimeseriesChart from '../components/TimeseriesChart';

function StatCard({ label, value, helper }: { label: string; value: string | number; helper?: string }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {label}
        </Typography>
        <Typography variant="h4" fontWeight={700}>
          {value}
        </Typography>
        {helper && (
          <Typography variant="caption" color="text.secondary">
            {helper}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function ApiDashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [bucket, setBucket] = useState('day');
  const [points, setPoints] = useState<TimeseriesPoint[]>([]);
  const [version, setVersion] = useState<VersionInfo | null>(null);
  const [health, setHealth] = useState<DbHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      assessmentsApi.stats(),
      healthApi.version().catch(() => null),
      healthApi.db().catch(() => null),
    ])
      .then(([s, v, h]) => {
        setStats(normalizeStatsPayload(s));
        setVersion(v);
        setHealth(h);
      })
      .catch(() => setError('Failed to load stats'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    assessmentsApi
      .timeseries({ bucket })
      .then((r) => setPoints(r.points))
      .catch(() => {});
  }, [bucket]);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!stats) return null;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700}>
          API Dashboard
        </Typography>
        {version && (
          <Stack direction="row" spacing={1}>
            <Chip size="small" label={`API v${version.apiVersion}`} variant="outlined" />
            {version.lastUpdated && (
              <Chip
                size="small"
                label={`Data updated ${new Date(version.lastUpdated).toLocaleDateString()}`}
                variant="outlined"
              />
            )}
            {health && (
              <Chip
                size="small"
                color={health.status === 'ok' ? 'success' : 'error'}
                label={`DB ${health.status}${health.ping?.ms != null ? ` · ${health.ping.ms}ms` : ''}`}
                variant="outlined"
              />
            )}
          </Stack>
        )}
      </Box>

      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} sm={4}>
          <StatCard label="Total Requests" value={stats.totals.totalRequests.toLocaleString()} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="Avg Response Time"
            value={`${Math.round(stats.totals.avgResponseMs ?? 0)} ms`}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            label="Avg Filings Returned"
            value={Math.round(stats.totals.avgFilings ?? 0)}
          />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 4 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" fontWeight={600}>
            Request volume
          </Typography>
          <TextField
            select
            size="small"
            label="Bucket"
            value={bucket}
            onChange={(e) => setBucket(e.target.value)}
            sx={{ width: 130 }}
          >
            <MenuItem value="hour">Hour</MenuItem>
            <MenuItem value="day">Day</MenuItem>
            <MenuItem value="week">Week</MenuItem>
            <MenuItem value="month">Month</MenuItem>
          </TextField>
        </Stack>
        <TimeseriesChart points={points} />
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Typography variant="h6" fontWeight={600} mb={1}>
            Requests by API Key
          </Typography>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Key Label</TableCell>
                  <TableCell align="right">Requests</TableCell>
                  <TableCell>Last Used</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.byKey.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No data yet
                    </TableCell>
                  </TableRow>
                ) : (
                  stats.byKey.map((row) => (
                    <TableRow key={row._id}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell align="right">{row.requests.toLocaleString()}</TableCell>
                      <TableCell>
                        {row.lastUsed ? new Date(row.lastUsed).toLocaleString() : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Typography variant="h6" fontWeight={600} mb={1}>
            Database
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            {health ? (
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Connection</Typography>
                  <Chip
                    size="small"
                    label={health.connectionState}
                    color={health.connectionState === 'connected' ? 'success' : 'warning'}
                  />
                </Stack>
                {health.host && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Host</Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {health.host}
                    </Typography>
                  </Stack>
                )}
                {health.name && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Database</Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {health.name}
                    </Typography>
                  </Stack>
                )}
                <Box pt={1} borderTop={1} borderColor="divider">
                  <Typography variant="caption" color="text.secondary">
                    Collection counts
                  </Typography>
                  {Object.entries(health.collections).map(([k, v]) => (
                    <Stack key={k} direction="row" justifyContent="space-between" mt={0.25}>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{k}</Typography>
                      <Typography variant="caption">{v.toLocaleString()}</Typography>
                    </Stack>
                  ))}
                </Box>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">No data</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
